/**
 * Open dialogs, drawers and sheets, topmost last. Only the topmost one reacts to Escape and keeps Tab inside
 * itself, so:
 *   - Escape in a dropdown inside a dialog closes the dropdown (it calls preventDefault), not the dialog;
 *   - a confirmation opened over a dialog closes alone, and the dialog stays with what you typed;
 *   - Tab never wanders to the page behind.
 *
 *   useEffect(() => (open ? openLayer({ panel: () => ref.current, onEscape: close }) : undefined), [open]);
 */
type Layer = { panel: () => HTMLElement | null; onEscape: () => void };

const stack: Layer[] = [];
let listening = false;

const FOCUSABLE =
  'a[href],button:not([disabled]),input:not([disabled]):not([type="hidden"]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])';

function onKey(e: KeyboardEvent) {
  const top = stack[stack.length - 1];
  if (!top) return;
  if (e.key === "Escape") {
    if (e.defaultPrevented) return; // something inside (an open dropdown) already used it
    e.preventDefault();
    top.onEscape();
    return;
  }
  if (e.key !== "Tab" || e.defaultPrevented) return;
  const panel = top.panel();
  if (!panel) return;
  const items = Array.from(panel.querySelectorAll<HTMLElement>(FOCUSABLE)).filter((el) => el.offsetParent !== null || el === document.activeElement);
  if (!items.length) return;
  const first = items[0];
  const last = items[items.length - 1];
  const active = document.activeElement as HTMLElement | null;
  const inside = active && panel.contains(active);
  if (e.shiftKey && (active === first || !inside)) {
    e.preventDefault();
    last.focus();
  } else if (!e.shiftKey && (active === last || !inside)) {
    e.preventDefault();
    first.focus();
  }
}

export function openLayer(layer: Layer): () => void {
  if (typeof document === "undefined") return () => {};
  if (!listening) {
    document.addEventListener("keydown", onKey);
    listening = true;
  }
  const before = document.activeElement as HTMLElement | null;
  stack.push(layer);
  return () => {
    const i = stack.lastIndexOf(layer);
    if (i !== -1) stack.splice(i, 1);
    // Back to where you were (the button that opened it), unless focus already moved somewhere sensible.
    if (before && document.contains(before) && (!document.activeElement || document.activeElement === document.body)) {
      try {
        before.focus({ preventScroll: true });
      } catch {
        /* element can't take focus any more */
      }
    }
  };
}

/** True when this layer is the topmost one (for components that handle their own keys). */
export function isTopLayer(panel: HTMLElement | null): boolean {
  const top = stack[stack.length - 1];
  return Boolean(top && panel && top.panel() === panel);
}
